import { Component, Element, Event, EventEmitter, h, Listen, Prop, State } from "@stencil/core";
import pfx from "../../utils/style-class-prefix-helper";
import { AnchorElement } from "./constants/anchor-types";
import { scrollToElement } from "../../utils/scroll-helper";
import { isMobile } from "../../utils/screen-helper";
import { KeyIds } from "../../constants/keys";
import { StyledComponent } from "../../decorators/styled-component";

// import { InitLogging } from "../../decorators/init-logging";

@Component({
  tag: "eon-ui-anchor-navigation",
  styleUrl: "anchor-navigation.scss",
  shadow: true
})
export class AnchorNavigation {
  // @InitLogging() sentryTransaction;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiAnchorNavigationElement;

  @State() anchorElements: AnchorElement[] = [];
  @State() isMobileDevice: boolean;

  /**
   * Set the initial position
   */
  @Prop() initialPosition: number = 600;

  /**
   * Set the fix position
   */
  @Prop() fixPosition: number = 120;

  /**
   * Set the offset for scrolling position
   */
  @Prop({ mutable: true }) offset: number;

  /**
   * Raised when the component has been loaded.
   */
  @Event() anchorNavigationLoad: EventEmitter<string>;

  /**
   * Raised when the component has been clicked.
   */
  @Event() anchorNavigationClick: EventEmitter<string>;

  componentWillLoad() {
    this.onViewportSizeChange();
    this.onScroll();
    // Anchor Navigation shall be operative for all the viewports except Mobile
    if (!this.isMobileDevice) {
      this.initNavigation();
    }
  }

  componentDidLoad() {
    this.anchorNavigationLoad.emit();
  }

  @Listen("resize", { target: "window" })
  onViewportSizeChange() {
    this.isMobileDevice = isMobile();
    if (this.isMobileDevice && this.anchorElements.length === 0) {
      this.initNavigation();
    }
  }

  @Listen("resize", { target: "window" })
  onWindowResize() {
    this.getActiveAnchorElement();
  }

  @Listen("scroll", { target: "window" })
  onScroll() {
    const anchorNav = this.hostElement?.shadowRoot?.querySelector(".eonui-anchor-nav");
    const fixNav = `${pfx("fix")}`;
    // Please Improve this code
    if (anchorNav) {
      if (window.scrollY > this.initialPosition - this.fixPosition) {
        anchorNav.classList.add(fixNav);
      } else {
        anchorNav.classList.remove(fixNav);
      }
    }
    this.getActiveAnchorElement();
  }

  initNavigation() {
    this.setOffset();
    this.anchorElements = this.getAnchorElements();

    setTimeout(() => {
      this.getActiveAnchorElement();
    }, 1000);
  }

  getAnchorElements(): AnchorElement[] {
    let anchorElements: AnchorElement[] = [];

    document.querySelectorAll("[data-anchor-id]").forEach((anchorElement: HTMLElement) => {
      if (anchorElement.dataset.anchorId && anchorElement.dataset.anchorTitle) {
        anchorElements.push({
          element: anchorElement,
          id: anchorElement.dataset.anchorId,
          title: anchorElement.dataset.anchorTitle,
          active: false
        });
      }
    });

    return anchorElements;
  }

  getActiveAnchorElement() {
    let scrollTop = window.scrollY;
    this.anchorElements.forEach((anchorElement) => {
      const targetOffsetTop = anchorElement.element.offsetTop - this.getMarginTop(anchorElement.element);
      if (targetOffsetTop + this.offset <= scrollTop + 1) {
        this.changeActiveAnchorElement(anchorElement.id);
      }
    });
  }

  getMarginTop = (element: HTMLElement): number => {
    const styles = window.getComputedStyle(element);
    return parseFloat(styles["marginTop"]);
  };

  setOffset() {
    if (!this.offset) {
      this.offset =
        -1 * parseInt(window.getComputedStyle(document.querySelector("body")).getPropertyValue("padding-top"), 10);
    }
  }

  changeActiveAnchorElement(activeAnchorId: string) {
    this.anchorElements.forEach((anchorElement) => {
      anchorElement.active = false;
    }); //reset active
    const index = this.anchorElements.findIndex((anchorElement) => anchorElement.id === activeAnchorId);
    const newAnchorElements = [...this.anchorElements];
    newAnchorElements[index].active = true;

    this.anchorElements = newAnchorElements;
  }

  scrollToAnchorElement(anchorElement: AnchorElement) {
    const element = anchorElement.element;
    this.setOffset();

    const scroll = () => {
      scrollToElement(element, {
        scrollContainer: window,
        offset: this.offset
      });
    };

    scroll();
    this.anchorNavigationClick.emit(element.getAttribute("data-anchor-title"));
  }

  private handleItemKeydown(e) {
    switch (e.key) {
      case KeyIds.ESCAPE:
        e.preventDefault();
        (e.currentTarget as HTMLElement).blur();
        break;
      default:
        break;
    }
  }
  render() {
    if (!this.isMobileDevice) {
      return (
        <div
          class={pfx({
            "anchor-nav": true
          })}
          style={{
            "--initPosition": `${this.initialPosition}px`,
            "--fixPosition": `${this.fixPosition}px`
          }}
        >
          <div class={pfx("anchor-nav__wrapper")}>
            <ul class={pfx("anchor-nav__list")}>
              {this.anchorElements.map((anchorElement) => {
                return (
                  <li class={pfx({ "anchor-nav__item": true, "anchor-nav__item--active": anchorElement.active })}>
                    <button
                      class={pfx({ "anchor-nav__container": true })}
                      onClick={() => this.scrollToAnchorElement(anchorElement)}
                      onKeyDown={(event: KeyboardEvent) => {
                        event.key == KeyIds.ENTER && this.scrollToAnchorElement(anchorElement);
                        this.handleItemKeydown(event);
                      }}
                      tabIndex={0}
                    >
                      <span
                        class={pfx({ "anchor-nav__icon": true, "anchor-nav__icon--active": anchorElement.active })}
                      ></span>
                      <span class={pfx("anchor-nav__link")}>{anchorElement.title}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      );
    }
    return;
  }
}
